'use client';

import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useState } from 'react';

import { WishlistButton } from '@/components/customer/WishlistButton';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';
import { FormatPrice } from '@/components/ui/FormatPrice';
import type { FeaturedBadgeVariant, FeaturedPlacementSummary } from '@/lib/api/clients/featured-content';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import { detectRegion } from '../utils/regionColors';
import { HorizonOverlay } from './PropertyCard';

// =============================================================================
// SHARED TYPES
// =============================================================================

export interface PropertyCardStyleProps {
  /** Property data */
  property: {
    id: string;
    name: string;
    description?: string | null;
    location?: string;
    status?: 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'DELETED';
    listingStatus?: string | null;
    listingType?: string | null;
    primaryImageUrl?: string | null;
    bedrooms?: number | null;
    bathrooms?: number | null;
    totalFloorArea?: number | null;
    currency?: string | null;
    currentAskingCurrency?: string | null;
    currentAskingPrice?: number | null;
    attributeValues?: {
      askingPrice?: {
        valueNumber?: number | null;
      };
    } | null;
  };
  /** Locale for routing */
  locale?: string;
  /** Show price */
  showPrice?: boolean;
  /** Show wishlist button */
  showWishlistButton?: boolean;
  /** Featured placements for badges */
  featuredPlacements?: FeaturedPlacementSummary[];
  /** Show featured badge */
  showFeaturedBadge?: boolean;
  /** Custom click handler (overrides link) */
  onClick?: () => void;
  /** Additional className */
  className?: string;
}

// Helper to get price and currency
function getPropertyPrice(property: PropertyCardStyleProps['property']) {
  const price = property.attributeValues?.askingPrice?.valueNumber ?? property.currentAskingPrice;
  const currency = property.currentAskingCurrency ?? property.currency ?? DEFAULT_CURRENCY;
  return { price, currency, hasPrice: price && price > 0 };
}

// Helper to get location
function getPropertyLocation(property: PropertyCardStyleProps['property']) {
  return property.location || property.description || 'Location';
}

// =============================================================================
// CARD STYLE 1: CLASSIC (The Horizon)
// =============================================================================

export interface ClassicCardProps extends PropertyCardStyleProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * ClassicCard (The Horizon)
 * 
 * Sample UI Design (EXACT MATCH):
 * - Portrait 3:4 aspect ratio
 * - Floating "View" button with spinning border in center
 * - Title and location overlaid at bottom left
 * - HorizonOverlay with flag colors on hover
 * - Image zoom on hover (2000ms)
 */
export const ClassicCard = memo(function ClassicCard({
  property,
  locale = 'en',
  showPrice: _showPrice = false,
  showWishlistButton = true,
  featuredPlacements = [],
  showFeaturedBadge = true,
  onClick,
  className = '',
  size = 'md',
}: ClassicCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const location = getPropertyLocation(property);
  const region = detectRegion(location);
  const isFrance = region === 'provence';
  const isSold = property.status === 'ARCHIVED'
    || property.status === 'INACTIVE'
    || property.listingStatus === 'SOLD';
  
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  // Size classes
  const titleSizes = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl md:text-4xl',
  };
  
  const viewButtonSizes = {
    sm: { active: 'w-16 h-16', text: 'text-sm' },
    md: { active: 'w-24 h-24', text: 'text-xl' },
    lg: { active: 'w-28 h-28', text: 'text-2xl' },
  };

  const CardContent = (
    <div 
      className={`group relative w-full aspect-3/4 overflow-hidden bg-stone-200 cursor-pointer ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image with Zoom Effect */}
      {property.primaryImageUrl ? (
        <Image 
          src={property.primaryImageUrl}
          alt={property.name}
          fill
          className={`object-cover transition-transform duration-2000 ease-out ${isHovered ? 'scale-105' : 'scale-100'}`}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />
      ) : (
        <div className="absolute inset-0 bg-linear-to-br from-stone-300 to-stone-200" />
      )}

      {/* Horizon Overlay */}
      <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />

      {/* Bottom Content */}
      <div className="absolute bottom-8 left-8 text-white z-40">
        <h3 className={`font-serif ${titleSizes[size]} drop-shadow-lg`}>{property.name}</h3>
        <p className="text-xs uppercase tracking-widest mt-2 opacity-90 drop-shadow-md">{location}</p>
      </div>
      
      {/* Floating View Button */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 flex items-center justify-center pointer-events-none">
        <div className={`relative flex items-center justify-center transition-all duration-700 ease-[0.19,1,0.22,1] ${isHovered ? `${viewButtonSizes[size].active} opacity-100` : 'w-12 h-12 opacity-0 translate-y-4'}`}>
          <div className="absolute inset-0 border-[0.5px] border-white/60 rounded-full animate-[spin_12s_linear_infinite]" />
          <span className={`text-white font-serif italic ${viewButtonSizes[size].text} tracking-widest drop-shadow-md`}>View</span>
        </div>
      </div>

      {/* Wishlist Button */}
      {showWishlistButton && !isSold && (
        <div className="absolute top-6 left-6 z-50">
          <WishlistButton
            entityType="PROPERTY"
            entityId={property.id}
            variant="solid"
            size="md"
            className="bg-white/90 hover:bg-white shadow-sm"
          />
        </div>
      )}

      {/* Status Badge */}
      {isSold && (
        <div className="absolute top-6 left-6 z-50">
          <span className="text-[10px] tracking-[0.25em] uppercase px-4 py-2 bg-white text-stone-900 font-sans">
            Sold
          </span>
        </div>
      )}

      {/* Featured Badge */}
      {primaryFeatured?.badgeVariant && !isSold && (
        <div className="absolute top-6 right-6 z-50">
          <FeaturedBadge
            variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
            label={primaryFeatured.label}
            size="sm"
          />
        </div>
      )}
    </div>
  );

  if (onClick) {
    return <div onClick={onClick}>{CardContent}</div>;
  }

  return <Link href={`/${locale}/properties/${property.id}`}>{CardContent}</Link>;
});

// =============================================================================
// CARD STYLE 2: CINEMA (Wide)
// =============================================================================

export interface CinemaCardProps extends PropertyCardStyleProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * CinemaCard (Wide)
 * 
 * Sample UI Design (EXACT MATCH):
 * - 16:9 aspect ratio (video format)
 * - Title large at bottom left
 * - Location and price reveal on hover
 * - Arrow indicator at bottom right
 * - HorizonOverlay with flag colors
 */
export const CinemaCard = memo(function CinemaCard({
  property,
  locale = 'en',
  showPrice = true,
  showWishlistButton = true,
  featuredPlacements = [],
  showFeaturedBadge = true,
  onClick,
  className = '',
  size = 'md',
}: CinemaCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const location = getPropertyLocation(property);
  const { price, currency, hasPrice } = getPropertyPrice(property);
  const region = detectRegion(location);
  const isFrance = region === 'provence';
  const isSold = property.status === 'ARCHIVED'
    || property.status === 'INACTIVE'
    || property.listingStatus === 'SOLD';
  
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  // Size classes
  const titleSizes = {
    sm: 'text-xl md:text-2xl',
    md: 'text-3xl md:text-4xl',
    lg: 'text-4xl md:text-5xl',
  };

  const CardContent = (
    <div 
      className={`group relative w-full aspect-video overflow-hidden bg-stone-200 cursor-pointer ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image with Zoom Effect */}
      {property.primaryImageUrl ? (
        <Image 
          src={property.primaryImageUrl}
          alt={property.name}
          fill
          className={`object-cover transition-transform duration-2000 ease-out ${isHovered ? 'scale-105' : 'scale-100'}`}
          sizes="(max-width: 768px) 100vw, 80vw"
        />
      ) : (
        <div className="absolute inset-0 bg-linear-to-br from-stone-300 to-stone-200" />
      )}

      {/* Horizon Overlay */}
      <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />

      {/* Bottom Content Bar */}
      <div className="absolute bottom-8 left-8 right-8 text-white z-40 flex justify-between items-end">
        <div>
          <h3 className={`font-serif ${titleSizes[size]} drop-shadow-lg`}>{property.name}</h3>
          <div className={`flex items-center gap-4 mt-2 transition-all duration-700 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <p className="text-xs uppercase tracking-widest opacity-90">{location}</p>
            {showPrice && hasPrice && (
              <>
                <span className="w-px h-3 bg-white/50" />
                <FormatPrice 
                  amount={price!} 
                  currency={currency} 
                  size="sm" 
                  className="text-white! text-xs uppercase tracking-widest opacity-90" 
                />
              </>
            )}
          </div>
        </div>
        <ArrowRight className={`transition-transform duration-500 ${isHovered ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'}`} />
      </div>

      {/* Wishlist Button */}
      {showWishlistButton && !isSold && (
        <div className="absolute top-6 left-6 z-50">
          <WishlistButton
            entityType="PROPERTY"
            entityId={property.id}
            variant="solid"
            size="md"
            className="bg-white/90 hover:bg-white shadow-sm"
          />
        </div>
      )}

      {/* Status Badge */}
      {isSold && (
        <div className="absolute top-6 left-6 z-50">
          <span className="text-[10px] tracking-[0.25em] uppercase px-4 py-2 bg-white text-stone-900 font-sans">
            Sold
          </span>
        </div>
      )}

      {/* Featured Badge */}
      {primaryFeatured?.badgeVariant && !isSold && (
        <div className="absolute top-6 right-6 z-50">
          <FeaturedBadge
            variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
            label={primaryFeatured.label}
            size="sm"
          />
        </div>
      )}
    </div>
  );

  if (onClick) {
    return <div onClick={onClick}>{CardContent}</div>;
  }

  return <Link href={`/${locale}/properties/${property.id}`}>{CardContent}</Link>;
});

// =============================================================================
// CARD STYLE 3: EDITORIAL (Overlap)
// =============================================================================

export interface EditorialCardProps extends PropertyCardStyleProps {
  /** Image alignment */
  imageAlign?: 'left' | 'right';
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * EditorialCard (Overlap)
 * 
 * Sample UI Design (EXACT MATCH):
 * - 4:5 aspect ratio image (90% width on desktop)
 * - Overlapping white text box at bottom right
 * - Location, title, price, and "Explore" CTA in box
 * - Decorative colored line animates on hover
 * - Box lifts slightly on hover
 */
export const EditorialCard = memo(function EditorialCard({
  property,
  locale = 'en',
  showPrice = true,
  showWishlistButton = true,
  featuredPlacements = [],
  showFeaturedBadge = true,
  onClick,
  className = '',
  imageAlign = 'left',
  size = 'md',
}: EditorialCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const location = getPropertyLocation(property);
  const { price, currency, hasPrice } = getPropertyPrice(property);
  const region = detectRegion(location);
  const isFrance = region === 'provence';
  const isSold = property.status === 'ARCHIVED'
    || property.status === 'INACTIVE'
    || property.listingStatus === 'SOLD';
  
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  // Size classes
  const titleSizes = {
    sm: 'text-xl md:text-2xl',
    md: 'text-2xl md:text-3xl',
    lg: 'text-3xl md:text-4xl',
  };

  // Alignment classes
  const imageAlignClasses = imageAlign === 'right' ? 'ml-auto' : '';
  const boxPositionClasses = imageAlign === 'right' ? 'left-0 md:left-4' : 'right-0 md:right-4';

  const CardContent = (
    <div 
      className={`group relative w-full cursor-pointer p-4 md:p-8 ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container */}
      <div className={`relative w-full aspect-4/5 md:w-[90%] overflow-hidden bg-stone-200 ${imageAlignClasses}`}>
        {property.primaryImageUrl ? (
          <Image 
            src={property.primaryImageUrl}
            alt={property.name}
            fill
            className={`object-cover transition-transform duration-2000 ease-out ${isHovered ? 'scale-105' : 'scale-100'}`}
            sizes="(max-width: 768px) 100vw, 66vw"
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-br from-stone-300 to-stone-200" />
        )}
        
        {/* Horizon Overlay */}
        <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />

        {/* Wishlist Button */}
        {showWishlistButton && !isSold && (
          <div className="absolute top-6 left-6 z-50">
            <WishlistButton
              entityType="PROPERTY"
              entityId={property.id}
              variant="solid"
              size="md"
              className="bg-white/90 hover:bg-white shadow-sm"
            />
          </div>
        )}

        {/* Featured Badge */}
        {primaryFeatured?.badgeVariant && !isSold && (
          <div className="absolute top-6 right-6 z-50">
            <FeaturedBadge
              variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
              label={primaryFeatured.label}
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Overlapping Text Box */}
      <div className={`absolute bottom-0 ${boxPositionClasses} w-3/4 md:w-1/2 bg-stone-50 p-6 shadow-2xl transition-transform duration-700 ease-out group-hover:-translate-y-2 z-40`}>
        <p className="text-[10px] uppercase tracking-widest text-stone-400 mb-2">{location}</p>
        <h3 className={`font-serif ${titleSizes[size]} text-stone-900 leading-none mb-4`}>{property.name}</h3>
        <div className="flex justify-between items-center border-t border-stone-200 pt-4">
          {showPrice && hasPrice ? (
            <FormatPrice 
              amount={price!} 
              currency={currency} 
              size="sm" 
              className="text-stone-900! font-sans text-xs font-medium" 
            />
          ) : (
            <span />
          )}
          <span className={`text-[10px] uppercase tracking-widest transition-colors duration-300 flex items-center gap-2 ${isHovered ? 'text-stone-900' : 'text-stone-400'}`}>
            {isSold ? 'Sold' : 'Explore'}
            <ArrowUpRight size={12} className={`transition-transform duration-300 ${isHovered ? 'translate-x-0.5 -translate-y-0.5' : ''}`} />
          </span>
        </div>
        {/* Decorative Line on Box Top */}
        <div 
          className={`absolute top-0 left-0 h-0.5 transition-all duration-700 ${isHovered ? 'w-full' : 'w-0'}`} 
          style={{ backgroundColor: isFrance ? '#002654' : '#EA2839' }} 
        />
      </div>
    </div>
  );

  if (onClick) {
    return <div onClick={onClick}>{CardContent}</div>;
  }

  return <Link href={`/${locale}/properties/${property.id}`}>{CardContent}</Link>;
});

// =============================================================================
// CARD STYLE 4: MINIMAL (Text Focus)
// =============================================================================

export interface MinimalCardProps extends PropertyCardStyleProps {
  /** Show image */
  showImage?: boolean;
  /** Index number to display */
  index?: number;
}

/**
 * MinimalCard (Text Focus)
 * 
 * Clean typographic card with optional image
 * - Large serif title with italic hover
 * - Metadata row with region indicator
 * - Optional small image thumbnail
 */
export const MinimalCard = memo(function MinimalCard({
  property,
  locale = 'en',
  showPrice = true,
  showWishlistButton = false,
  onClick,
  className = '',
  showImage = true,
  index,
}: MinimalCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const location = getPropertyLocation(property);
  const { price, currency, hasPrice } = getPropertyPrice(property);
  const region = detectRegion(location);
  const isFrance = region === 'provence';
  const isSold = property.status === 'ARCHIVED'
    || property.status === 'INACTIVE'
    || property.listingStatus === 'SOLD';

  const CardContent = (
    <div 
      className={`group relative w-full cursor-pointer border-b border-stone-200 py-8 transition-all duration-300 hover:pl-4 ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start gap-6">
        {/* Optional Thumbnail */}
        {showImage && property.primaryImageUrl && (
          <div className="relative w-20 h-20 shrink-0 overflow-hidden bg-stone-200">
            <Image 
              src={property.primaryImageUrl}
              alt={property.name}
              fill
              className={`object-cover transition-transform duration-700 ${isHovered ? 'scale-110' : 'scale-100'}`}
              sizes="80px"
            />
            {/* Mini Horizon Strip */}
            <div className="absolute bottom-0 left-0 w-full h-0.5 flex">
              {isFrance ? (
                <>
                  <div className={`h-full flex-1 bg-[#002654] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
                  <div className={`h-full flex-1 bg-white transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '50ms' }} />
                  <div className={`h-full flex-1 bg-[#ED2939] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }} />
                </>
              ) : (
                <>
                  <div className={`h-full flex-1 bg-[#EA2839] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} />
                  <div className={`h-full flex-1 bg-[#1A206D] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '40ms' }} />
                  <div className={`h-full flex-1 bg-[#FFD500] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '80ms' }} />
                  <div className={`h-full flex-1 bg-[#00A551] transition-opacity duration-500 ${isHovered ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '120ms' }} />
                </>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-baseline gap-4">
            {index !== undefined && (
              <span className="text-xs font-sans text-stone-400 group-hover:text-stone-900 transition-colors shrink-0">
                0{index + 1}
              </span>
            )}
            <h3 className={`font-serif text-2xl md:text-3xl text-stone-900 leading-none transition-all duration-300 truncate ${isHovered ? 'italic' : ''}`}>
              {property.name}
            </h3>
            {showPrice && hasPrice && (
              <FormatPrice 
                amount={price!} 
                currency={currency} 
                size="sm" 
                className="text-stone-900! font-sans text-sm shrink-0" 
              />
            )}
          </div>
          
          <div className={`flex items-center gap-3 mt-3 overflow-hidden transition-all duration-300 ${isHovered ? 'h-6 opacity-100' : 'h-0 opacity-0'}`}>
            <p className="text-xs uppercase tracking-widest text-stone-500">{location}</p>
            {property.bedrooms && (
              <>
                <span className="w-px h-2.5 bg-stone-300" />
                <span className="text-xs uppercase tracking-widest text-stone-500">{property.bedrooms} Beds</span>
              </>
            )}
            {isSold && (
              <>
                <span className="w-px h-2.5 bg-stone-300" />
                <span className="text-xs uppercase tracking-widest text-stone-400">Sold</span>
              </>
            )}
          </div>
        </div>

        {/* Wishlist */}
        {showWishlistButton && !isSold && (
          <div className="shrink-0">
            <WishlistButton
              entityType="PROPERTY"
              entityId={property.id}
              variant="outline"
              size="sm"
            />
          </div>
        )}

        {/* Arrow */}
        <ArrowUpRight 
          size={20} 
          className={`shrink-0 text-stone-400 transition-all duration-300 ${isHovered ? 'text-stone-900 translate-x-1 -translate-y-1' : ''}`} 
        />
      </div>
    </div>
  );

  if (onClick) {
    return <div onClick={onClick}>{CardContent}</div>;
  }

  return <Link href={`/${locale}/properties/${property.id}`}>{CardContent}</Link>;
});

// =============================================================================
// CARD STYLE 5: SPOTLIGHT (Large Feature)
// =============================================================================

export interface SpotlightCardProps extends PropertyCardStyleProps {
  /** Whether this is the active/focused card */
  isActive?: boolean;
  /** Variant for different contexts */
  variant?: 'carousel' | 'hero';
}

/**
 * SpotlightCard (Large Feature)
 * 
 * Designed for hero sections and carousels
 * - 3:4 portrait for carousel, full for hero
 * - Large title with gradient overlay
 * - Active state shows full info
 */
export const SpotlightCard = memo(function SpotlightCard({
  property,
  locale = 'en',
  showPrice = true,
  showWishlistButton = true,
  featuredPlacements = [],
  showFeaturedBadge = true,
  onClick,
  className = '',
  isActive = true,
  variant = 'carousel',
}: SpotlightCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const location = getPropertyLocation(property);
  const { price, currency, hasPrice } = getPropertyPrice(property);
  const region = detectRegion(location);
  const isFrance = region === 'provence';
  const isSold = property.status === 'ARCHIVED'
    || property.status === 'INACTIVE'
    || property.listingStatus === 'SOLD';
  
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  const showDetails = isActive || isHovered;

  const CardContent = (
    <div 
      className={`group relative overflow-hidden bg-stone-800 cursor-pointer transition-all duration-700 ease-[0.22,1,0.36,1] ${
        variant === 'carousel' 
          ? `aspect-3/4 ${isActive ? 'scale-100 opacity-100' : 'scale-90 opacity-40 grayscale'}` 
          : 'w-full h-full'
      } ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image */}
      {property.primaryImageUrl ? (
        <Image 
          src={property.primaryImageUrl}
          alt={property.name}
          fill
          className={`object-cover transition-transform duration-2000 ease-out ${showDetails ? 'scale-105' : 'scale-100'}`}
          sizes="(max-width: 768px) 80vw, 600px"
        />
      ) : (
        <div className="absolute inset-0 bg-linear-to-br from-stone-700 to-stone-900" />
      )}
      
      {/* Horizon Overlay */}
      <HorizonOverlay isHovered={showDetails} isFrance={isFrance} />

      {/* Gradient Overlay for Text Legibility */}
      <div className="absolute inset-0 bg-linear-to-t from-stone-900 via-stone-900/60 to-transparent opacity-60" />

      {/* Content */}
      {showDetails && (
        <div className="absolute bottom-0 left-0 right-0 p-8 text-white z-40">
          <h3 className="font-serif text-3xl md:text-4xl">{property.name}</h3>
          <p className="text-xs uppercase tracking-widest mt-2 opacity-80">{location}</p>
          {showPrice && hasPrice && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <FormatPrice 
                amount={price!} 
                currency={currency} 
                size="md" 
                className="text-white!" 
              />
            </div>
          )}
        </div>
      )}

      {/* Wishlist Button */}
      {showWishlistButton && !isSold && isActive && (
        <div className="absolute top-6 left-6 z-50">
          <WishlistButton
            entityType="PROPERTY"
            entityId={property.id}
            variant="solid"
            size="md"
            className="bg-white/90 hover:bg-white shadow-sm"
          />
        </div>
      )}

      {/* Featured Badge */}
      {primaryFeatured?.badgeVariant && !isSold && isActive && (
        <div className="absolute top-6 right-6 z-50">
          <FeaturedBadge
            variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
            label={primaryFeatured.label}
            size="sm"
          />
        </div>
      )}
    </div>
  );

  if (onClick) {
    return <div onClick={onClick}>{CardContent}</div>;
  }

  return <Link href={`/${locale}/properties/${property.id}`}>{CardContent}</Link>;
});

// =============================================================================
// CARD STYLE 6: DECK (3D Stack)
// =============================================================================

export interface DeckCardProps extends PropertyCardStyleProps {
  /** Card position in stack (0 = front) */
  stackPosition?: number;
  /** Index number for display */
  index?: number;
}

/**
 * DeckCard (3D Stack)
 * 
 * Designed for the PropertyDeck component
 * - Split layout: 3/4 image, 1/4 info
 * - Shine effect on hover
 * - Shows horizon only on front card
 */
export const DeckCard = memo(function DeckCard({
  property,
  locale = 'en',
  showPrice = true,
  onClick,
  className = '',
  stackPosition = 0,
  index = 0,
}: DeckCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const location = getPropertyLocation(property);
  const { price, currency, hasPrice } = getPropertyPrice(property);
  const region = detectRegion(location);
  const isFrance = region === 'provence';
  const isActive = stackPosition === 0;
  const isSold = property.status === 'ARCHIVED'
    || property.status === 'INACTIVE'
    || property.listingStatus === 'SOLD';

  const CardContent = (
    <div 
      className={`relative w-full h-full bg-stone-100 flex flex-col rounded-sm overflow-hidden shadow-2xl cursor-pointer group ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Area */}
      <div className="relative h-3/4 overflow-hidden">
        {property.primaryImageUrl ? (
          <Image 
            src={property.primaryImageUrl}
            alt={property.name}
            fill
            className="object-cover"
            sizes="400px"
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-br from-stone-300 to-stone-200" />
        )}
        
        {/* Dynamic Shine Effect */}
        <div 
          className="absolute inset-0 bg-linear-to-tr from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" 
          style={{ backgroundSize: '200% 200%' }} 
        />
        
        {/* Shadow Gradient */}
        <div className="absolute inset-0 bg-linear-to-t from-stone-900/80 to-transparent opacity-60" />

        {/* Horizon Strip - Only on Active */}
        {isActive && <HorizonOverlay isHovered={isHovered} isFrance={isFrance} />}

        {/* Sold Badge */}
        {isSold && (
          <div className="absolute top-6 left-6 z-20">
            <span className="text-[10px] tracking-[0.25em] uppercase px-4 py-2 bg-white text-stone-900 font-sans">
              Sold
            </span>
          </div>
        )}
      </div>
      
      {/* Card Body */}
      <div className="h-1/4 p-6 md:p-8 flex flex-col justify-between bg-white relative">
        <div>
          <div className="flex justify-between items-start mb-1">
            <h3 className="font-serif text-2xl md:text-3xl text-stone-900 truncate pr-4">{property.name}</h3>
            <span className="text-xs font-sans text-stone-300">0{index + 1}</span>
          </div>
          <p className="text-xs uppercase tracking-widest text-stone-500">{location}</p>
        </div>
        <div className="flex justify-between items-center text-xs font-medium border-t border-stone-100 pt-4">
          {showPrice && hasPrice ? (
            <FormatPrice amount={price!} currency={currency} size="sm" className="text-stone-900!" />
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2 text-stone-400 group-hover:text-stone-900 transition-colors">
            <span className="uppercase tracking-widest">{isSold ? 'Sold' : 'Explore'}</span>
            <ArrowRight size={14} />
          </div>
        </div>
      </div>
    </div>
  );

  if (onClick) {
    return <div onClick={onClick}>{CardContent}</div>;
  }

  return <Link href={`/${locale}/properties/${property.id}`}>{CardContent}</Link>;
});
